/**
 * AWOPS-3496 — the single slugifier for automation selectors.
 *
 * `data-testid` values must be kebab-case (PROJECT-STANDARDS §16), but the strings they are
 * built from are not: table slugs are SCREAMING_SNAKE (`APPLICANT`), dialog ids likewise
 * (`ADD_APPLICANT_MODAL`), column ids are snake_case (`first_name`), option values can be
 * anything (`ORG_ADMIN`), and action labels are human text (`"Send Email"`). Normalising here
 * means every package emits the *same* id for the same input — which matters, because a spec
 * that finds `-option-org-admin` in one dropdown and `-option-ORG_ADMIN` in another is a spec
 * nobody can write.
 *
 * This used to be copy-pasted privately into ~20 files, with at least one copy that skipped the
 * camelCase split and so disagreed with the rest.
 *
 * @example toTestId('APPLICANT') // 'applicant'
 * @example toTestId('ADD_APPLICANT_MODAL') // 'add-applicant-modal'
 * @example toTestId('first_name') // 'first-name'
 * @example toTestId('Send Email') // 'send-email'
 * @example toTestId('assessmentShortName') // 'assessment-short-name'
 * @example toTestId(undefined) // ''
 */
export function toTestId(value: string | number | undefined | null): string {
    if (value === undefined || value === null || value === '') return ''
    return String(value)
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2') // camelCase → camel-Case
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
}

/**
 * Builds `{base}-{suffix}`, or `undefined` when there is no base.
 *
 * Returning `undefined` rather than the bare suffix is deliberate: a shared component with no
 * scope must emit **nothing**, so a missing scope fails loudly in a test instead of quietly
 * matching another instance on the same page.
 *
 * @example childTestId('applicant-column-settings', 'btn') // 'applicant-column-settings-btn'
 * @example childTestId('', 'btn') // undefined
 */
export function childTestId(base: string | undefined | null, suffix: string): string | undefined {
    return base ? `${base}-${suffix}` : undefined
}
